import React, { useCallback, useEffect, useState } from 'react';
import { helpers, styled } from 'react-free-style';
import OutsideClickHandler from 'react-outside-click-handler';
import { elements, icons } from '@united-talent-agency/julius-frontend-components';
import { colors } from '@united-talent-agency/components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import InlineEditableContact from './InlineEditableContact';
import Notes from './Notes';
import CallHistoryTable from './CallHistoryTable';
import { useToasts } from 'react-toast-notifications';
import { CALL_ROW } from '../../support/cypressTags';
import RowCheckbox from './RowCheckbox';
import Status from './Status';
import Starred from './Starred';
import CallRowName from './CallRowName';
import CompanyName from './CompanyName';
import CallDate from './CallDate';
import StaticNotes from './StaticNotes';
import Actions from './Actions';
import { CallTableRow } from './styles';
import useAddCallContact from '../../hooks/useAddCallContact';

const Component = ({
  item,
  hideBulkEdits = false,
  desk,
  recipient,
  selected,
  statuses,
  onSelectedChange,
  onSave,
  onDeleteItem,
  onEditItem,
  styles,
  deskContacts,
}) => {
  const addCallContact = useAddCallContact();
  const { addToast } = useToasts();
  const { notesModal: showNotesModal = true, companyName: showCompanyName = false } = useFlags();
  // States
  const [history, setHistory] = useState(false);
  const [call, setCall] = useState(item);
  const [updatedAt, setUpdatedAt] = useState(item.updatedAt);

  useEffect(() => {
    const newUpdate = new Date(item.updatedAt).getTime();
    if (!updatedAt || updatedAt < newUpdate) {
      setUpdatedAt(newUpdate);
    }

    setCall(item);
  }, [item, updatedAt]);

  const updateCall = useCallback(
    async (values) => {
      delete values?.recipientType;
      delete values?.extension;
      delete values?.contact?.description;
      delete values?.contact?.private;
      delete values?.person;

      // Ensure all notes have a valid note field (required by GraphQL schema)
      if (values.notes) {
        values.notes = values.notes.map((note) => ({
          ...note,
          note: note.note || '',
        }));
      }

      await addCallContact(values);

      // Remove fields after addCallContact uses them (not part of GraphQL schema)
      delete values?.isPersonalContact;
      delete values?.deskId;

      return onSave({
        _id: call._id,
        ...values,
      })
        .then(setCall)
        .catch((e) => {
          setCall(values);
          addToast(e.message, { appearance: 'error', autoDismiss: true });
          return { error: 'failed update' };
        });
    },
    [addCallContact, addToast, call._id, onSave]
  );

  return (
    <CallTableRow
      id={`call-row-${call._id}`}
      data-occurrence-date={call.occurrence_date}
      data-updated-at={call.updatedAt}
      data-cy={CALL_ROW.CALL_ROW}
      onDoubleClick={() => (showNotesModal ? null : onEditItem(call))}
      selected={selected}
    >
      {!hideBulkEdits && <RowCheckbox onChange={onSelectedChange} selected={selected} />}
      <Status callTodo={call} statuses={statuses} onSave={updateCall} styles={styles} />
      <Starred favorite={call.favorite} onSave={updateCall} />
      <CallRowName styles={styles} item={call} />

      {showCompanyName && <CompanyName item={call} styles={styles} />}
      <InlineEditableContact
        id="inline-editable-contact"
        deskId={desk?._id}
        deskAddressBookEntryContacts={deskContacts}
        callTodo={call}
        recipient={recipient}
        onSave={updateCall}
      />
      <CallDate occurrenceDate={call.occurrence_date} onSave={updateCall} styles={styles} />
      {showNotesModal ? (
        <Notes notes={call?.notes} onSave={updateCall} />
      ) : (
        <StaticNotes notes={call.description} id={call._id} styles={styles} />
      )}
      <Actions
        id={call._id}
        showHistory={() => setHistory(true)}
        onEdit={() => onEditItem(call)}
        onDelete={() => onDeleteItem(call)}
      />
      {history && (
        <td className={styles.historyCell}>
          <OutsideClickHandler onOutsideClick={() => setHistory(false)}>
            <CallHistoryTable callId={call._id} />
          </OutsideClickHandler>
        </td>
      )}
    </CallTableRow>
  );
};

const withStyles = styled({
  name: {
    minWidth: '116px',
  },
  person: {
    display: 'flex',
    cursor: 'default',
    alignItems: 'center',
    maxWidth: '100%',
  },
  personName: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  company: {
    ['@container call-list-container (max-width: 1150px)']: {
      display: 'none',
    },
  },
  contact: {
    cursor: 'pointer',
    textOverflow: 'ellipsis',
    fontWeight: 300,
  },
  date: {
    whiteSpace: 'nowrap',
    maxWidth: '100%',

    '&&': {
      paddingLeft: '8px',
      paddingRight: '8px',
    },

    '&:hover': {
      cursor: 'pointer',
    },
  },
  note: {
    display: 'flex',
    alignItems: 'center',
  },
  noteContent: {
    width: '350px',
    float: 'left',
    overflowWrap: 'break-word',
    paddingLeft: '5px',
  },
  noteBorder: {
    display: 'inline-block',
    width: 1,
    height: 20,
    backgroundColor: colors.whiteSmoke,
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
  },
  button: helpers.merge(elements.reset, elements.actionable, {
    padding: 10,
    lineHeight: 0,
  }),
  editIcon: icons.pencil,
  declineIcon: icons.cross,
  deleteIcon: icons.trashcan,
  status: {
    paddingLeft: 7,
    paddingRight: 7,
    width: 55,
  },
  faOutline: {
    textShadow: '0px 0px 1px black',
  },
  iconSize: {
    fontSize: 14,
  },
  link: {
    color: '#2187B9',
    '&:hover': {
      color: '#2187B9',
    },
    maxWidth: '100%',
  },
  maxWidth: {
    maxWidth: '100%',
  },
  historyCell: {
    position: 'relative',
  },
});

const CallRow = withStyles(Component);

export default CallRow;
